(ns graph-ui.web.work-item
  "The widget kinds of the work item page (IKC3/PLAN-ikc-v3-080-01/ITEM,
   screen 3 of the plan): :inline-edit, :field-list, :criteria-list,
   :status-menu and :development-panel. Each one is declared in
   dsl/contexts/ui/meta-ui/v2.1-SNAPSHOT/, and each declaration holds the
   reasons. This namespace draws them.

   A CLICK EDITS IN PLACE. A value is text until a click opens its editor.
   Enter or Save sends ONE gesture, and Escape or Cancel sends nothing. The
   bound value then shows what the store holds, so the widget keeps no copy
   of the data after the send.

   THE RULES ARE IN THE GATE. Each control sends its gesture, and the
   command decides. A refusal is the sentence of the status line of the
   surface, never a control that is grey.

   EVERY CHILD LIST IS SPREAD WITH `into`. A bare vector of hiccup as one
   child is read by Reagent as a component call (CLAUDE.md)."
  (:require [clojure.string :as str]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.reopen :as reopen]
            [graph-ui.web.widgets :as widgets]
            [reagent.core :as r]))

;; @plan PLAN-ikc-v3-080-01-the-tracker-v2
;; @adr ADR-graph-ui-v0-013-01-one-layout-model

(def reply-timeout-ms
  "How long the page waits for the reply of the bus before it says so."
  15000)

(defn reply-sentence
  "PURE: the sentence a reply of the bus carries when it is a refusal, or nil
   when it is an acceptance. One reader for every refusal: the handler's own
   words, a bus-level rejection (schema, authz) and a lost connection
   (`widgets/refusal-of`, the seam of the block editor and the file drop)."
  [reply]
  (let [m (when (map? reply) reply)]
    (if (= "disconnected" (some-> (:error m) name))
      "The page lost the connection to the bus. Nothing was saved."
      (when-let [why (widgets/refusal-of reply)]
        (str why (when-not (str/ends-with? why ".") "."))))))

(defn- fire!
  "Dispatch the command of `template` with `value` as $value.

   STALL, never VANISH. The reply is read. `done` is called ONCE with nil when
   the bus accepted, or with the sentence when the bus refused, rejected or
   did not answer within `:timeout-ms`. A caller keeps what the person typed
   or ticked on screen until `done` runs."
  ([bus session-id template value] (fire! bus session-id template value nil))
  ([bus session-id template value {:keys [done timeout-ms]}]
   (let [settled (atom false)
         settle! (fn [sentence]
                   (when (compare-and-set! settled false true)
                     (when done (done sentence))))]
     (if-not (and bus template)
       (settle! "This page has no command for that. Nothing was saved.")
       (do
         (js/setTimeout
          #(settle! (str "The bus did not answer in "
                         (js/Math.round (/ (or timeout-ms reply-timeout-ms) 1000))
                         " seconds. Nothing is known to be saved."))
          (or timeout-ms reply-timeout-ms))
         (js-invoke bus "dispatch"
                    (bind/fill-command template {'$session-id session-id '$value value})
                    (fn [reply]
                      (settle! (reply-sentence (js->clj reply :keywordize-keys true))))))))))

(defn notice-line
  "The sentence of a refusal that the bus itself sent, under the widget."
  [sentence]
  (when sentence
    [:div {:class "text-xs text-[var(--danger)] py-1" :role "alert" :data-notice true} sentence]))

(defn- actions [{:keys [on-gesture]}] on-gesture)

(def ^:private muted "text-[var(--muted)]")
(def ^:private field-class
  (str "w-full rounded border border-[var(--border)] bg-[var(--field)] px-2 py-1 "
       "text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"))
(def ^:private small-button
  (str "rounded border border-[var(--border)] px-2 py-0.5 text-xs "
       "text-[var(--text)] hover:bg-[var(--hover)]"))
(def ^:private primary-button
  (str "rounded px-2 py-0.5 text-xs bg-[var(--accent)] text-white"))

(defn- editor-buttons [save! cancel!]
  [:div {:class "flex gap-2 mt-1"}
   [:button {:class primary-button :on-click (fn [e] (.preventDefault e) (save!))} "Save"]
   [:button {:class small-button :on-click (fn [e] (.preventDefault e) (cancel!))} "Cancel"]])

;; ---------------------------------------------------------------------------
;; :inline-edit
;; ---------------------------------------------------------------------------

(defn widget-inline-edit
  "A text that a click edits in place. Gesture :submit, $value the text."
  [_props _bus _session-id]
  (let [editing (r/atom nil)]
    (fn [{:keys [value placeholder label multiline variant read-only] :as props} bus session-id]
      (let [submit  (:submit (actions props))
            heading (= :heading (keyword variant))
            text    (str value)
            save!   (fn []
                      (let [v @editing]
                        (reset! editing nil)
                        (when (not= v text) (fire! bus session-id submit v))))
            cancel! #(reset! editing nil)]
        [:div {:class "flex flex-col min-w-0"}
         (when label
           [:div {:class (str "text-xs font-semibold uppercase tracking-wide pb-1 " muted)} label])
         (if (some? @editing)
           [:div {:class "flex flex-col"}
            (if multiline
              [:textarea {:class (str field-class " min-h-[6em]")
                          :auto-focus true
                          :value @editing
                          :on-change #(reset! editing (.. % -target -value))
                          :on-key-down (fn [e] (when (= "Escape" (.-key e)) (cancel!)))}]
              [:input {:class (str field-class (when heading " text-xl font-semibold"))
                       :auto-focus true
                       :value @editing
                       :on-change #(reset! editing (.. % -target -value))
                       :on-key-down (fn [e]
                                      (case (.-key e)
                                        "Enter"  (do (.preventDefault e) (save!))
                                        "Escape" (cancel!)
                                        nil))}])
            [editor-buttons save! cancel!]]
           [:div {:class (str (if heading "text-xl font-semibold text-[var(--heading)] " "text-sm ")
                              "whitespace-pre-wrap break-words rounded px-1 -mx-1 "
                              (when-not (or read-only (not submit)) "cursor-text hover:bg-[var(--hover)] ")
                              (when (str/blank? text) muted))
                  :role (when-not read-only "button")
                  :tab-index (when-not read-only 0)
                  :title (when-not read-only "Click to edit")
                  :on-click (fn [_] (when (and submit (not read-only)) (reset! editing text)))
                  :on-key-down (fn [e] (when (and submit (not read-only) (= "Enter" (.-key e)))
                                         (reset! editing text)))}
            (if (str/blank? text) (or placeholder "Click to add") text)])]))))

;; ---------------------------------------------------------------------------
;; :field-list
;; ---------------------------------------------------------------------------

(defn- field-row
  "One row of a :field-list."
  [_row _edit _bus _session-id]
  (let [editing (r/atom nil)]
    (fn [{:keys [field label value editor options]} edit bus session-id]
      (let [editor  (keyword (or editor :none))
            text    (str value)
            can?    (and edit (not= :none editor))
            send!   (fn [v]
                      (reset! editing nil)
                      (when (not= v text) (fire! bus session-id edit {:field field :value v})))
            cancel! #(reset! editing nil)]
        [:div {:class "grid grid-cols-[minmax(7em,40%)_1fr] gap-2 py-1 items-start text-sm"
               :data-field field}
         [:div {:class muted} label]
         (cond
           (and can? (= :select editor))
           (into [:select {:class (str field-class " py-0.5")
                           :value text
                           :on-change #(send! (.. % -target -value))}]
                 (cons [:option {:value "" :key "-"} "—"]
                       (for [{v :value l :label} options]
                         ^{:key v} [:option {:value v} l])))

           (some? @editing)
           [:div {:class "flex flex-col"}
            (if (= :lines editor)
              [:textarea {:class (str field-class " min-h-[5em]") :auto-focus true :value @editing
                          :on-change #(reset! editing (.. % -target -value))
                          :on-key-down (fn [e] (when (= "Escape" (.-key e)) (cancel!)))}]
              [:input {:class field-class :auto-focus true :value @editing
                       :type (case editor :number "number" :day "date" "text")
                       :on-change #(reset! editing (.. % -target -value))
                       :on-key-down (fn [e]
                                      (case (.-key e)
                                        "Enter"  (do (.preventDefault e) (send! @editing))
                                        "Escape" (cancel!)
                                        nil))}])
            [editor-buttons #(send! @editing) cancel!]]

           :else
           [:div {:class (str "whitespace-pre-wrap break-words rounded px-1 -mx-1 "
                              (when can? "cursor-text hover:bg-[var(--hover)] ")
                              (when (str/blank? text) muted))
                  :title (when can? "Click to edit")
                  :on-click (fn [_] (when can? (reset! editing text)))}
            (if (str/blank? text) "—" text)])]))))

(defn widget-field-list
  "The fields of one thing. Gesture :edit, $value {:field :value}."
  [{:keys [items title empty-label] :as props} bus session-id]
  (let [edit (:edit (actions props))]
    (when (or (seq items) empty-label)
      [:div {:class "flex flex-col"}
       (when title
         [:div {:class (str "text-xs font-semibold uppercase tracking-wide pb-1 " muted)} title])
       (if (empty? items)
         [:div {:class (str "text-sm " muted)} empty-label]
         (into [:div {:class "flex flex-col"}]
               (for [row items]
                 ^{:key (:field row)} [field-row row edit bus session-id])))])))

;; ---------------------------------------------------------------------------
;; :criteria-list
;; ---------------------------------------------------------------------------

(def ^:private verifier-tone
  {"george"     "text-[var(--danger)] border-[var(--danger)]"
   "supervisor" "text-[var(--accent)] border-[var(--accent)]"
   "test"       "text-[var(--muted)] border-[var(--border)]"})

(def test-verifier-reason
  "Why the box of a criterion that a test proves is grey."
  "A test proves this criterion. A person cannot tick it here.")

(defn tick-checked?
  "PURE: the state the box shows. The intended value of a tick in flight
   shows until the bus answers, then the value the store holds."
  [proven? pending id]
  (if (contains? pending id) (boolean (get pending id)) (boolean proven?)))

(defn tick!
  "A tick: show the intended value as pending, send it, and on the reply
   return to the server value. A refusal leaves its sentence in `notice`."
  [bus session-id prove id proven? pending notice opts]
  (let [want (not (tick-checked? proven? @pending id))]
    (reset! notice nil)
    (swap! pending assoc id want)
    (fire! bus session-id prove {:criterion-id id :proven want}
           (assoc opts :done (fn [sentence]
                               (swap! pending dissoc id)
                               (reset! notice sentence))))))

(defn- criterion-row
  [{:keys [id text verifier verifier-label state proven-by proven-at verifier-options]}
   prove set-verifier bus session-id pending notice]
  (let [proven?  (= "proven" state)
        by-test? (= "test" verifier)
        pend?    (contains? @pending id)
        shown?   (tick-checked? proven? @pending id)]
    [:div {:class "flex items-start gap-2 py-1 text-sm" :data-criterion id
           :data-pending (when pend? "true")}
     [:input {:type "checkbox" :checked shown? :class (str "mt-1 " (when (or pend? by-test?) "ikc-dim"))
              :disabled (or by-test? pend?)
              :aria-busy pend?
              :title (cond by-test? test-verifier-reason pend? "Waiting for the bus")
              :aria-label (if proven? "Open the criterion again" "Prove the criterion")
              :on-change (fn [_] (tick! bus session-id prove id proven? pending notice nil))}]
     [:div {:class "flex-1 min-w-0"}
      [:div {:class (str "break-words " (when shown? "line-through text-[var(--muted)]"))} text]
      (when by-test? [:div {:class (str "text-xs " muted)} test-verifier-reason])
      (when pend? [:div {:class (str "text-xs " muted)} "Waiting for the bus…"])
      (when (and proven? (not pend?))
        [:div {:class (str "text-xs " muted)}
         (str "Proven by " proven-by (when proven-at (str " · " proven-at)))])]
     (if (and set-verifier (seq verifier-options))
       (into [:select {:class (str "text-[10px] font-semibold uppercase rounded border px-1 bg-transparent "
                                   (get verifier-tone verifier ""))
                       :aria-label "The verifier of the criterion"
                       :title "Who proves this criterion"
                       :value verifier
                       :on-change #(fire! bus session-id set-verifier
                                          {:criterion-id id :verifier (.. % -target -value)})}]
             (for [{v :value l :label} verifier-options]
               ^{:key v} [:option {:value v} (str "Verifier: " l)]))
       [:span {:class (str "text-[10px] font-semibold uppercase rounded border px-1 "
                           (get verifier-tone verifier ""))}
        (str "Verifier: " verifier-label)])]))

(defn add!
  "Send the text of `draft` as a new criterion. ONE SEND AT A TIME: while a
   send waits for its reply, `busy` is true and another Enter does nothing.
   The words stay in `draft` until the bus accepts them."
  [bus session-id add draft busy notice opts]
  (when (and (not @busy) (not (str/blank? @draft)))
    (let [sent @draft]
      (reset! busy true)
      (reset! notice nil)
      (fire! bus session-id add sent
             (assoc opts :done (fn [sentence]
                                 (reset! busy false)
                                 (reset! notice sentence)
                                 (when (and (nil? sentence) (= sent @draft))
                                   (reset! draft ""))))))))

(defn widget-criteria-list
  "The acceptance criteria. Gestures :prove, :set-verifier and :add."
  [_props _bus _session-id]
  (let [draft (r/atom "")
        pending (r/atom {})
        busy (atom false)
        notice (r/atom nil)]
    (fn [{:keys [items title add-placeholder empty-label] :as props} bus session-id]
      (let [{:keys [prove set-verifier add]} (actions props)]
        [:div {:class "flex flex-col"}
         (when title
           [:div {:class (str "text-xs font-semibold uppercase tracking-wide pb-1 " muted)} title])
         (if (empty? items)
           (when empty-label [:div {:class (str "text-sm " muted)} empty-label])
           (into [:div {:class "flex flex-col"}]
                 (for [c items]
                   ^{:key (:id c)} [criterion-row c prove set-verifier bus session-id pending notice])))
         [notice-line @notice]
         (when add
           [:input {:class (str field-class " mt-1 text-sm")
                    :placeholder (or add-placeholder "Add a criterion, then press Enter")
                    :value @draft
                    :on-change #(reset! draft (.. % -target -value))
                    :on-key-down (fn [e]
                                   (when (and (= "Enter" (.-key e)) (not (str/blank? @draft)))
                                     (.preventDefault e)
                                     (add! bus session-id add draft busy notice nil)))}])]))))

;; ---------------------------------------------------------------------------
;; :status-menu
;; ---------------------------------------------------------------------------

(defn widget-status-menu
  "The status and its moves. Gesture :move, $value the target category."
  [_props _bus _session-id]
  (let [open? (r/atom false)]
    (fn [{:keys [value shown] :as props} bus session-id]
      (let [move  (:move (actions props))
            {:keys [label transitions item-id]} value
            ;; REOPEN ASKS FOR A REASON (IKC3/PLAN-ikc-v3-080-01/REPORT,
            ;; screen 1b). The transition :reopen opens its dialog, and the
            ;; dialog sends :workflow/reopen. Each other move fires at once.
            reopen? (fn [to] (some #(and (= to (:to %)) (= "reopen" (:name %))) transitions))
            go!   (fn [to]
                    (reset! open? false)
                    (if (and item-id (reopen? to))
                      (reopen/open! {:bus bus :item-id (uuid item-id)})
                      (fire! bus session-id move to)))
            n     (or shown 2)]
        [:div {:class "flex flex-wrap items-center gap-2 relative"}
         [:button {:class (str primary-button " text-sm px-3 py-1 font-semibold")
                   :aria-haspopup "true"
                   :on-click #(swap! open? not)}
          (str (or label "—") " ▾")]
         ;; A MOVE WITH A RESOLUTION SENDS ITS :value, the name of its
         ;; transition (IKC3/PLAN-ikc-v3-080-01/FLOW34, decision 34).
         (into [:span {:class (str "text-xs " muted)}]
               (interpose " · "
                          (for [{:keys [to label value]} (take n transitions)]
                            ^{:key (or value to)}
                            [:a {:class "cursor-pointer hover:underline" :on-click #(go! (or value to))} label])))
         (when @open?
           (into [:div {:class (str "absolute top-full left-0 mt-1 z-[var(--layer-overlay)] min-w-[12em] flex flex-col "
                                    "rounded border border-[var(--border)] bg-[var(--panel)] "
                                    "shadow-[var(--elevation-floating)] py-1")
                        :style {:background "var(--panel)"}
                        :role "menu"}]
                 (if (seq transitions)
                   (for [{:keys [to label value]} transitions]
                     ^{:key (or value to)}
                     [:button {:class "text-left text-sm px-3 py-1 hover:bg-[var(--hover)]"
                               :role "menuitem" :on-click #(go! (or value to))}
                      label])
                   [[:div {:class (str "text-sm px-3 py-1 " muted)} "No move is declared from here."]])))]))))

;; ---------------------------------------------------------------------------
;; :development-panel
;; ---------------------------------------------------------------------------

(defn widget-development-panel
  "The code of the work on one work item. No gesture."
  [{:keys [value title empty-label]} _bus _session-id]
  (let [{:keys [pull-request pull-request-url branch worktree machine session
                merge-commit boot-commit deployed]} value
        rows (remove (comp str/blank? str second)
                     [["Branch" branch] ["Worktree" worktree] ["Machine" machine]
                      ["Session" session] ["Merge commit" merge-commit]
                      ["Running commit" boot-commit] ["Deploy" deployed]])]
    [:div {:class "flex flex-col rounded border border-[var(--border)] p-3 gap-1 text-sm"}
     [:div {:class "font-semibold"} (or title "Development")]
     (when pull-request
       [:div
        (if pull-request-url
          [:a {:class "text-[var(--accent)] underline" :href pull-request-url :target "_blank"
               :rel "noopener"}
           (str "Pull request #" pull-request)]
          (str "Pull request #" pull-request))])
     (into [:div {:class "flex flex-col"}]
           (for [[l v] rows]
             ^{:key l} [:div [:span {:class muted} (str l ": ")] (str v)]))
     (when (and (nil? pull-request) (empty? rows))
       [:div {:class muted} (or empty-label "No pull request, branch or deploy yet.")])]))
